<template>
  <div class="uk-container">
    <template v-if="!appointment || !dentist">
      <div class="uk-alert-danger uk-width-medium uk-margin-auto uk-text-center" uk-alert>
        <p>Oops! <br> Something went wrong.</p>
      </div>
    </template>

    <template v-else>
      <div class="uk-card uk-card-default uk-width-large uk-margin-auto">
        <div class="uk-card-header uk-text-center">
          <h3 class="uk-card-title">Appointment</h3>
          <div v-if="$slots.header" class="uk-text-center">
            <slot name="header" />
          </div>
          <p class="uk-text-meta">{{ appointment.id }}</p>
        </div>

        <div class="uk-card-body uk-text-center uk-padding-remove card-content">
          <div class="section">
            <div class="section-title">Customer</div>
            <div class="section-content">
              <div class="item">
                <div class="item-title">Name</div>
                <div class="item-content">
                  <div class="composite">
                    <div class="composite-part">
                      <div class="value">{{ appointment.customer.name.first }}</div>
                      <div class="label">First</div>
                    </div>
                    <div class="composite-part">
                      <div class="value">{{ appointment.customer.name.last }}</div>
                      <div class="label">Last</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="item">
                <div class="item-title">Phone</div>
                <div class="item-content">
                  {{ appointment.customer.contact.phone }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Email</div>
                <div class="item-content">
                  <template v-if="appointment.customer.contact.email">
                    {{ appointment.customer.contact.email }}
                  </template>
                  <template v-else>
                    <span class="uk-label label-secondary">Not Provided</span>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Emergency</div>
            <div class="section-content">
              <div class="item">
                <div class="item-title">Description</div>
                <div class="item-content" data-cy="description">
                  {{ appointment.emergency.description }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Complaints</div>
                <div class="item-content">
                  <template v-for="(complaint, index) in appointment.emergency.complaints">
                    <div :key="index">
                      {{ $helpers.complaints.find(c => c.id === complaint).value }}
                    </div>
                  </template>
                </div>
              </div>
              <div class="item">
                <div class="item-title">Features</div>
                <div class="item-content">
                  <template v-if="appointment.emergency.features.length">
                    <template v-for="(feature, index) in appointment.emergency.features">
                      <div :key="index">
                        {{ $helpers.practiceFeatures.find(f => f.id === feature).value }}
                      </div>
                    </template>
                  </template>
                  <template v-else>
                    <span class="uk-label label-secondary">None Requested</span>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Appointment</div>
            <div class="section-content">
              <div v-if="userType !== 'dentist'" class="item">
                <div class="item-title">Dentist</div>
                <div class="item-content">
                  {{ dentist.name }}
                </div>
              </div>
              <div v-if="userType !== 'dentist'" class="item">
                <div class="item-title">Location</div>
                <div class="item-content">
                  {{ dentist.location.house }} {{ dentist.location.street }}<br>
                  {{ dentist.location.postcode }}, {{ dentist.location.city }}
                </div>
              </div>
              <div v-if="userType !== 'dentist'" class="item">
                <div class="item-title">Area</div>
                <div class="item-content">
                  {{ $helpers.getAreaName(dentist.area) }}
                </div>
              </div>
              <div class="item">
                <div class="item-title">Date</div>
                <div class="item-content">
                  <div class="composite composite-spaced">
                    <div>
                      {{ $moment(appointment.dentist.data[dentist.id].date, 'YYMMDD').format('DD/MM/YYYY') }} - {{ this.$helpers.timeBits.find(b => b.id === appointment.dentist.data[dentist.id].bit).time.short }}
                    </div>
                    <div :uk-tooltip="`title: ${formatDateTime(appointment.dentist.data[dentist.id], true)}; pos: right; offset: 20;`" style="margin-left: 8px;"><div class="icon gg-info" /></div>
                  </div>
                </div>
              </div>
              <div v-if="userType === 'dentist'" class="item">
                <div class="item-title">Status</div>
                <div class="item-content">
                  <template v-if="appointment.dentist.data[dentist.id].status === 0">
                    <div class="uk-label uk-label-warning">Incomplete</div>
                  </template>

                  <template v-else-if="appointment.dentist.data[dentist.id].status === 1">
                    <div class="uk-label uk-label-success">Booked</div>
                  </template>

                  <template v-else-if="appointment.dentist.data[dentist.id].status === 2">
                    <div class="uk-label uk-label-danger">Cancelled</div>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div v-if="userType !== 'dentist'" class="section">
            <div class="section-title">Payment</div>
            <div v-if="appointment.payment" class="section-content">
              <div class="item">
                <div class="item-title">Status</div>
                <div class="item-content">
                  <template v-if="appointment.payment.status === 'succeeded'">
                    <div class="uk-label uk-label-success">Success</div>
                  </template>

                  <template v-else-if="appointment.payment.status === 'requires_payment_method'">
                    <div class="uk-label uk-label-warning">Incomplete</div>
                    <div class="uk-margin-top">Requires Payment Method</div>
                  </template>

                  <template v-else>
                    <div class="composite composite-spaced">
                      <div class="uk-label uk-label-danger">Error</div>
                      <div :uk-tooltip="`title: ${appointment.payment.status}; pos: right; offset: 20;`" style="margin-left: 8px;"><div class="icon gg-info" /></div>
                    </div>
                  </template>
                </div>
              </div>
              <div class="item">
                <div class="item-title">Date</div>
                <div class="item-content">
                  <div class="composite composite-spaced">
                    <div>
                      {{ $moment(appointment.payment.created.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss') }}
                    </div>
                    <div :uk-tooltip="`title: ${formatDateTime(appointment.payment.created.seconds)}; pos: right; offset: 20;`" style="margin-left: 8px;"><div class="icon gg-info" /></div>
                  </div>
                </div>
              </div>
              <div class="item">
                <div class="item-title">Amount</div>
                <div class="item-content">
                  {{ appointment.payment.amount|price }}
                </div>
              </div>
            </div>
            <div v-else class="section-content">
              <div class="background-danger">
                <div class="uk-text-bold">Error</div>
                <div>Please contact support!</div>
              </div>
            </div>
          </div>

          <div v-if="userType !== 'dentist' && refunds && refunds.length" class="section">
            <div class="section-title">Refunds</div>
            <div class="section-content">
              <div v-if="refunds.length > 1" class="item">
                <div class="item-title">Refunds Total</div>
                <div class="item-content">
                  {{ refunds.reduce((a, {value}) => a + value, 0)|price }}
                </div>
              </div>
              <template v-for="(refund, name, index) in appointment.refund">
                <div v-if="refund.reason" :key="index" class="item">
                  <div class="item-title">Refund {{ index + 1 }}</div>
                  <div class="item-content refund">
                    <p>Amount: {{ refund.value|price }}</p>
                    <p>Reason: {{ refund.reason }}</p>
                    <p>Date: {{ $moment(refund.date.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss') }}</p>
                    <p>Status: <span v-html="formatStatus(refund.status)" /></p>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div v-if="userType !== 'dentist'" class="section">
            <div class="section-title">Appointment Info</div>
            <div class="section-content">
              <div class="item">
                <div class="item-title">Status</div>
                <div class="item-content">
                  <template v-if="appointment.info.status === 0">
                    <div class="uk-label uk-label-warning">Incomplete</div>
                  </template>

                  <template v-else-if="appointment.info.status === 1">
                    <div class="uk-label uk-label-success">Booked</div>
                  </template>

                  <template v-else-if="appointment.info.status === 2">
                    <div class="uk-label uk-label-danger">Cancelled</div>
                  </template>
                </div>
              </div>
              <div class="item">
                <div class="item-title">Created</div>
                <div class="item-content">
                  <div class="composite composite-spaced">
                    <div>
                      {{ $moment(appointment.info.created.timestamp.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss') }}
                    </div>
                    <div :uk-tooltip="`title: ${formatDateTime(appointment.info.created.timestamp.seconds)}; pos: right; offset: 20;`" style="margin-left: 8px;"><div class="icon gg-info" /></div>
                  </div>
                </div>
              </div>
              <div class="item">
                <div class="item-title">Created by</div>
                <div class="item-content">
                  {{ operatorCreated.name.first }} {{ operatorCreated.name.last }}
                </div>
              </div>
              <div v-if="appointment.info.updated" class="item">
                <div class="item-title">Updated</div>
                <div class="item-content">
                  <div class="composite composite-spaced">
                    <div>
                      {{ $moment(appointment.info.updated.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss') }}
                    </div>
                    <div :uk-tooltip="`title: ${formatDateTime(appointment.info.updated.seconds)}; pos: right; offset: 20;`" style="margin-left: 8px;"><div class="icon gg-info" /></div>
                  </div>
                </div>
              </div>
              <div v-if="operatorUpdated" class="item">
                <div class="item-title">Updated by</div>
                <div class="item-content">
                  {{ operatorUpdated.name.first }} {{ operatorUpdated.name.last }}
                </div>
              </div>
              <div v-if="appointment.notes.other && appointment.notes.other.cancel" class="item">
                <div class="item-title">Cancel Reason</div>
                <div class="item-content">
                  {{ appointment.notes.other.cancel }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="userType !== 'dentist'" class="section">
            <div class="section-title">Operator Notes</div>
            <div class="section-content" style="padding: 10px;">
              {{ appointment.notes.operator || 'Not Available' }}
            </div>
          </div>

          <div v-if="$slots.bodyEnd" class="uk-card-footer uk-text-center">
            <slot name="bodyEnd" />
          </div>
        </div>

        <div v-if="$slots.footer" class="uk-card-footer uk-text-center">
          <slot name="footer" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import nowMixin from '@/utils/mixins/now'

export default {
  name: 'ComponentAppointmentInfo',

  filters: {
    // Format Price
    price: (value) => {
      if (!value) return ''
      const price = value.toString()
      return `£${price.slice(0, price.length - 2)}.${price.slice(price.length - 2, price.length)}`
    }
  },

  mixins: [nowMixin],

  props: {
    appointmentId: {
      type: String,
      required: true
    }
  },

  computed: {
    appointment() {
      return this.$store.getters['appointments/find'](this.appointmentId)
    },

    dentist() {
      return this.appointment.dentist && this.userType !== 'dentist' ? this.$store.getters['dentists/find'](this.appointment.info.dentist) : this.$store.getters['dentists/find'](this.$store.state.account.profile.dentist)
    },

    operatorCreated() {
      return this.appointment.info.created && this.appointment.info.created.user ? this.$store.getters['users/find'](this.appointment.info.created.user) : null
    },

    operatorUpdated() {
      return this.appointment.info.updated && this.appointment.info.updated.user ? this.$store.getters['users/find'](this.appointment.updated.user) : null
    },

    userType() {
      return this.$store.getters['account/type']
    },

    refunds() {
      return this.appointment.refund ? Object.values(this.appointment.refund) : []
    }
  },

  methods: {
    formatDateTime(date, special = false) {
      let response = null

      if (special) {
        const timestamp = `${date.date} ${this.$helpers.timeBits.find((b) => b.id === date.bit).time.short}`
        response = this.$moment(timestamp, 'YYMMDD HH:mm').fromNow()
      } else {
        response = this.$moment(date, 'X').fromNow()
      }

      return response
    },

    formatStatus(data) {
      let label = '<div class="uk-label uk-label-danger label-small">N/A</div>'
      const option = this.$helpers.status.request.find((o) => o.id === data)
      label = `<div class="uk-label uk-label-${option.class} label-small">${option.value}</div>`
      return label
    }
  }
}
</script>

<style lang="scss" scoped>
.background-danger {
  padding: 1em;
  background: $colour-danger;
  color: $colour-light;
}

.refund p {
  margin: 0;
}
</style>
